import { Injectable } from '@angular/core';
import { CognitiveTargetQuestion } from '../core-data-model/CognitiveTargetQuestion.model';
import { JobChecklistQuestion } from '../core-data-model/JobChecklistQuestion.model';
import { SetJobTargetStep } from '../core-data-model/SetJobTarget.interface';
import { Util } from '../core-utilities/Util';

type SerializedCognitive = {
    id: number;
    responseSelected: number;
};

type SerializedBehavioral = {
    id: number;
};

type SerializedCurrentPage = {
    step: SetJobTargetStep;
    page: number;
};

type SetJobTargetFlowSave = {
    behavioral: SerializedBehavioral[];
    cognitive: SerializedCognitive[];
    currentPage: SerializedCurrentPage;
    senderTakesJobAssessment: boolean;
};

@Injectable({ providedIn: 'root' })
export class SetJobTargetSaveService {
    private static localStorageSaveKey = 'setJobTargetFlowDataService:save';

    constructor(private util: Util) {}

    private serializeBehavioral(
        checklistQuestions: JobChecklistQuestion[]
    ): SerializedBehavioral[] {
        if (!checklistQuestions) {
            return null;
        }
        return checklistQuestions
            .filter((q) => q.checked)
            .map((q) => ({
                id: q.id
            }));
    }

    private serializeCognitive(
        cognitiveQuestions: CognitiveTargetQuestion[]
    ): SerializedCognitive[] {
        if (!cognitiveQuestions) {
            return null;
        }
        return cognitiveQuestions
            .filter((q) => q.responseSelected !== null)
            .map((q) => ({
                id: q.id,
                responseSelected: q.responseSelected
            }));
    }

    private serializeCurrentPage(
        step: SetJobTargetStep,
        page: number
    ): SerializedCurrentPage {
        return {
            step,
            page
        };
    }

    private getKey(jobId: string): string {
        return `${SetJobTargetSaveService.localStorageSaveKey}:${jobId}`;
    }

    private getData(jobId: string): SetJobTargetFlowSave {
        return JSON.parse(localStorage.getItem(this.getKey(jobId)));
    }

    inProgress(jobId: string): boolean {
        return !!this.getData(jobId);
    }

    clearSavedResponses(jobId: string): void {
        localStorage.removeItem(this.getKey(jobId));
    }

    saveProgress(
        jobId: string,
        checklistQuestions: JobChecklistQuestion[],
        cognitiveQuestions: CognitiveTargetQuestion[],
        step: SetJobTargetStep,
        page: number,
        senderTakesJobAssessment: boolean
    ): void {
        const existingData = this.getData(jobId);
        const save = {
            senderTakesJobAssessment,
            behavioral: checklistQuestions
                ? this.serializeBehavioral(checklistQuestions)
                : existingData && existingData.behavioral,
            cognitive: cognitiveQuestions
                ? this.serializeCognitive(cognitiveQuestions)
                : existingData && existingData.cognitive,
            currentPage: page
                ? this.serializeCurrentPage(step, page)
                : existingData && existingData.currentPage
        };
        localStorage.setItem(this.getKey(jobId), JSON.stringify(save));
    }

    applySavedChangesToBehavioral(
        jobId: string,
        checklistQuestions: JobChecklistQuestion[]
    ): JobChecklistQuestion[] {
        const cloned = this.util.array.cloneObjectArray(checklistQuestions);
        const loaded: SetJobTargetFlowSave = this.getData(jobId);
        if (loaded && loaded.behavioral) {
            loaded.behavioral.forEach((q) => {
                const cq = cloned.find(
                    (checklistQuestion) => checklistQuestion.id === q.id
                );
                cq.checked = true;
            });
        }
        return cloned;
    }

    applySavedChangesToCognitive(
        jobId: string,
        cognitiveQuestions: CognitiveTargetQuestion[]
    ): CognitiveTargetQuestion[] {
        const cloned = this.util.array.cloneObjectArray(cognitiveQuestions);
        const loaded: SetJobTargetFlowSave = this.getData(jobId);
        if (loaded && loaded.cognitive) {
            loaded.cognitive.forEach((q) => {
                const cognitiveQuestion = cloned.find((cq) => cq.id === q.id);
                cognitiveQuestion.responseSelected = q.responseSelected;
            });
        }
        return cloned;
    }

    getLastPageForJob(jobId: string): SerializedCurrentPage {
        const data = this.getData(jobId);
        if (!data) {
            return null;
        }
        return data.currentPage;
    }

    getSenderTakesJobAssessment(jobId: string): boolean {
        const data = this.getData(jobId);
        if (!data) {
            return null;
        }
        return data.senderTakesJobAssessment;
    }
}
